import {
  Badge,
  Button,
  Inline,
  InlineError,
  RouterLink,
  Stack,
  Text,
} from "../../ui";
import { VariableValueField } from "../variables/VariableValueField";
import {
  cascadeFlags,
  contentionMessages,
  declarationConflict,
  declarationConflictMessage,
  groupDeclarationsByKey,
} from "../../lib/variableCascade";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import {
  canonicalizeVariableSweepDrafts,
  type VariableDeclaration,
  type VariableSweepDraft,
} from "./simulationLaunchState";

export interface VariableSweepEditorProps {
  agentId: string;
  drafts: VariableSweepDraft[];
  onChange: (drafts: VariableSweepDraft[]) => void;
  variables: VariableDeclaration[];
}

function allowedValues(variable: VariableDeclaration): string[] {
  return (variable.current_revision.allowed_values ?? []).filter(
    (value): value is string => typeof value === "string",
  );
}

function nextDraftId(drafts: VariableSweepDraft[]): string {
  const sequence = drafts.reduce((maximum, draft) => {
    const match = /^set-(\d+)$/.exec(draft.id);
    return Math.max(maximum, match ? Number(match[1]) : 0);
  }, 0);
  return `set-${String(sequence + 1)}`;
}

export function VariableSweepEditor({
  agentId,
  drafts,
  onChange,
  variables,
}: VariableSweepEditorProps) {
  const variableGroups = groupDeclarationsByKey(variables);
  const canonicalDrafts = canonicalizeVariableSweepDrafts(drafts, variables);
  const declaredKeys = new Set(variableGroups.keys());
  const unknownKeys = [
    ...new Set(
      canonicalDrafts.flatMap((draft) =>
        Object.keys(draft.values).filter(
          (key) => !declaredKeys.has(unicodeCaseFold(key)),
        ),
      ),
    ),
  ];

  const patchValue = (draftId: string, key: string, value: string) => {
    onChange(
      canonicalDrafts.map((draft) =>
        draft.id === draftId
          ? {
              ...draft,
              values: {
                ...draft.values,
                [key]: value,
              },
            }
          : draft,
      ),
    );
  };

  const removeKey = (key: string) => {
    onChange(
      canonicalDrafts.map((draft) => ({
        ...draft,
        values: Object.fromEntries(
          Object.entries(draft.values).filter(([draftKey]) => draftKey !== key),
        ),
      })),
    );
  };

  return (
    <Stack gap={3}>
      {variables.length === 0 ? (
        <Inline gap={2}>
          <Text variant="muted">No variables available for overrides.</Text>
          <RouterLink
            params={{ agentId }}
            rel="noopener"
            search={{ new: 1 }}
            target="_blank"
            to="/agents/$agentId/variables"
          >
            Declare variable
          </RouterLink>
        </Inline>
      ) : null}
      {canonicalDrafts.map((draft, index) => (
        <Stack gap={2} key={draft.id}>
          <Inline align="center" gap={2}>
            <Text variant="caps">Override set {index + 1}</Text>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                onChange(
                  canonicalDrafts.filter((item) => item.id !== draft.id),
                );
              }}
            >
              Remove set
            </Button>
          </Inline>
          {[...variableGroups.values()].map((group) => {
            const variable = group[0];
            if (!variable) return null;
            const raw = draft.values[variable.key] ?? "";
            const valueType = variable.current_revision.value_type;
            const conflict = declarationConflict(group);
            const flags = cascadeFlags(group);
            const disabled = conflict !== "none";
            const control = (
              <VariableValueField
                allowedValues={allowedValues(variable)}
                disabled={disabled}
                emptyLabel="No override"
                label={variable.key}
                value={raw}
                valueType={valueType}
                onChange={(value) => {
                  patchValue(draft.id, variable.key, value);
                }}
              />
            );
            return (
              <Stack gap={1} key={unicodeCaseFold(variable.key)}>
                {group.length > 1 ? (
                  <Inline align="center" gap={2}>
                    <Badge>Contended</Badge>
                    <Text variant="muted">
                      {contentionMessages(group).join(" ")}
                    </Text>
                  </Inline>
                ) : null}
                {flags.isSensitive ? (
                  <Text as="span" variant="masked">
                    {control}
                  </Text>
                ) : (
                  control
                )}
                {conflict !== "none" ? (
                  <InlineError>
                    {/* The declaration winner is unruled; report and block. */}
                    {declarationConflictMessage(
                      variable.key,
                      group.length,
                      conflict,
                    )}
                  </InlineError>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      ))}
      {unknownKeys.map((key) => (
        <Stack gap={2} key={key}>
          <InlineError>
            {key} is undeclared. Declare it or remove the override to launch.
          </InlineError>
          <Inline>
            <Button
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => {
                removeKey(key);
              }}
            >
              Remove {key}
            </Button>
            {/* Law: docs/09a-frontend-screens.md (launch and template authoring). */}
            <RouterLink
              params={{ agentId }}
              rel="noopener"
              search={{ key, new: 1 }}
              target="_blank"
              to="/agents/$agentId/variables"
            >
              Declare {key}
            </RouterLink>
          </Inline>
        </Stack>
      ))}
      {variables.length > 0 ? (
        <Inline>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              onChange([
                ...canonicalDrafts,
                { id: nextDraftId(canonicalDrafts), values: {} },
              ]);
            }}
          >
            Add override set
          </Button>
        </Inline>
      ) : null}
    </Stack>
  );
}
